import React, { memo, useCallback, useMemo } from 'react';
import { AvatarWithInitials, DialogComponent, StatusLabel, Table } from 'common/components/index';
import messages from 'common/components/PersonProfile/messages';
import { IFilterSettings } from 'common/interfaces/filter';
import { IHeadCell, ITableParams, ITableRow } from 'common/interfaces/table';
import { IServisecHistoryItem } from 'modules/booking/interfaces';
import { IPageMeta } from 'common/interfaces/pagination';
import tableHeaders from 'common/messages/tableHeaders';
import useTimezoneMoment from 'common/hooks/useTimezoneMoment';
import { Box, Typography } from '@material-ui/core';
import ServicesHistoryPackageModal from '../ServicesHistoryPackageModal/ServicesHistoryPackageModal';
import { IBundleView } from 'modules/services/interfaces/packages';
import { IInventoryDetailedInfo } from 'modules/reports/interfaces/invoiceHistory';
import { DefaultStatus } from 'common/constants/campaign';
import IntlService from 'services/intl/IntlService';
import EmployeeLinkWithAvatar from 'common/components/ProfileLinkWithAvatar/EmployeeLinkWithAvatar';
import { DEFAULT_DATE_TIME_FORMAT } from 'common/constants/dateFormats';
import useRootSelector from 'common/hooks/useRootSelector';
import { selectIsMemberPortalUserLoggedIn } from 'memberPortalModules/authentication/state/selectors';
import { getInitials } from 'helpers/common';
import { makeEmployeeLabel } from 'utils/employee.utils';

const getHeaderOptions = (): IHeadCell[] => [
  {
    id: 'title',
    label: IntlService.formatMessage(tableHeaders.title),
    sort: false,
  },
  {
    id: 'date',
    label: IntlService.formatMessage(tableHeaders.sellDate),
    sort: true,
  },
  {
    id: 'startDate',
    label: IntlService.formatMessage(tableHeaders.startDate),
    sort: false,
  },
  {
    id: 'endDate',
    label: IntlService.formatMessage(tableHeaders.endDate),
    sort: false,
  },
  {
    id: 'salesperson',
    label: IntlService.formatMessage(tableHeaders.salesperson),
    sort: false,
  },
  {
    id: 'status',
    label: IntlService.formatMessage(tableHeaders.status),
    sort: true,
  },
];

interface IProps {
  onClose: () => void;
  packageId: string | null;
  inventoryId: string | null;
  servicesHistory?: {
    data: IServisecHistoryItem[];
    meta: IPageMeta;
  };
  isServicesHistoryLoading: boolean;
  setPackageId: (id: string | null) => void;
  setInventoryId: (id: string | null) => void;
  packageInfo?: IBundleView;
  isPackageInfoLoading: boolean;
  inventoryInfo?: IInventoryDetailedInfo;
  isInventoryInfoLoading: boolean;
  handleChangeTableProps: (tableProps: ITableParams) => void;
  tableParams: ITableParams;
  tableFilterSettings: IFilterSettings[];
}

const ServicesHistoryModal = ({
  onClose,
  inventoryId,
  packageId,
  setPackageId,
  setInventoryId,
  servicesHistory,
  isServicesHistoryLoading,
  packageInfo,
  isPackageInfoLoading,
  inventoryInfo,
  isInventoryInfoLoading,
  handleChangeTableProps,
  tableParams,
  tableFilterSettings,
}: IProps) => {
  const [timezoneMoment] = useTimezoneMoment();
  const isMemberPortal = useRootSelector(selectIsMemberPortalUserLoggedIn);

  const getRenderSalesperson = useCallback(
    salesperson => {
      if (!salesperson) {
        return '';
      }
      return isMemberPortal ? (
        <Box display="flex" alignItems="center">
          <AvatarWithInitials
            initials={getInitials(salesperson.firstName[0], salesperson.lastName[0])}
            imageUrl={salesperson.imageUrl}
            width="30px"
            height="30px"
            marginRight={0.5}
          />
          <Typography component="span" variant="body1">
            {makeEmployeeLabel(salesperson)}
          </Typography>
        </Box>
      ) : (
        <EmployeeLinkWithAvatar employee={salesperson} />
      );
    },
    [isMemberPortal],
  );

  const rows = useMemo(
    () =>
      servicesHistory?.data
        ? servicesHistory.data.map(
            (item: IServisecHistoryItem): ITableRow => ({
              id: item.id.toString(),
              cells: [
                {
                  label: '',
                  cellComponent: (
                    <Typography
                      color="primary"
                      variant="button"
                      onClick={() => {
                        setPackageId(item.id);
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      {item.title}
                    </Typography>
                  ),
                },
                {
                  label: timezoneMoment(item.date).format(DEFAULT_DATE_TIME_FORMAT),
                },
                {
                  label: timezoneMoment(item.startDate).format(DEFAULT_DATE_TIME_FORMAT),
                },
                {
                  label: timezoneMoment(item.endDate).format(DEFAULT_DATE_TIME_FORMAT),
                },
                {
                  label: '',
                  cellComponent: getRenderSalesperson(item.salesperson),
                },
                { label: <StatusLabel isActive={item.status === DefaultStatus.Active} /> },
              ],
            }),
          )
        : [],
    [servicesHistory, timezoneMoment, setPackageId, getRenderSalesperson],
  );

  return (
    <>
      <DialogComponent
        title={IntlService.formatMessage(messages.servicesHistoryTitle)}
        isOpen
        onClose={onClose}
        size="xl"
        hideFooterButtons
        loading={isServicesHistoryLoading}
      >
        <Table
          rows={rows}
          headerOptions={getHeaderOptions()}
          isLoading={isServicesHistoryLoading}
          page={servicesHistory?.meta.page}
          totalRows={servicesHistory?.meta.total}
          onChangeParams={handleChangeTableProps}
          tableParams={tableParams}
          filters={tableFilterSettings}
        />
      </DialogComponent>
      {!!packageId && (
        <ServicesHistoryPackageModal
          onClose={() => setPackageId(null)}
          packageInfo={packageInfo}
          isPackageInfoLoading={isPackageInfoLoading}
          setInventoryId={setInventoryId}
          inventoryId={inventoryId}
          inventoryInfo={inventoryInfo}
          isInventoryInfoLoading={isInventoryInfoLoading}
        />
      )}
    </>
  );
};

export default memo(ServicesHistoryModal);
